<template>
  <div class="offer-detail-map">
    <div
      ref="container"
      class="offer-detail-map__canvas"
      data-test="map"
    />
  </div>
</template>

<script>
  import { computed, defineComponent, onBeforeUnmount, onMounted, ref, toRefs, watch } from '@vue/composition-api'

  import { addZoomControl } from '@/services/GoogleMaps'
  import useMap from '@/composables/useMap'
  import useStore from '@/composables/useStore'

  export default defineComponent({
    name: 'OfferDetailMap',
    props: {
      hasZoomControls: {
        type: Boolean,
        default: false
      },
      hasScrollWheelZoom: {
        type: Boolean,
        default: false
      }
    },
    setup (props) {
      const { hasScrollWheelZoom, hasZoomControls } = toRefs(props)
      const { createMap, drawOffer, clear, retrieveItinerary, zoomToMarkers } = useMap()
      const store = useStore()

      const container = ref(null)

      const getCurrentOffer = computed(() => store.value.getters['offers/getCurrentOffer'])

      /**
       * @function showOffer
       * @param {object} offer
       */
      const showOffer = (offer) => {
        drawOffer(offer)
        retrieveItinerary(offer)
        zoomToMarkers()
      }

      onMounted(async () => {
        /**
         * Without the wheel, the map lets the page scroll under the pointer as
         * the Leaflet one did — `gestureHandling` is left alone, since its
         * cooperative mode would print "use ctrl + scroll to zoom" over a map
         * that never zoomed on the wheel in the first place.
         */
        const map = await createMap(container.value, hasScrollWheelZoom.value
          ? { scrollwheel: true, gestureHandling: 'greedy' }
          : { scrollwheel: false })
        if (!map) return

        if (hasZoomControls.value) addZoomControl(map)

        if (getCurrentOffer.value) showOffer(getCurrentOffer.value)
      })

      watch(getCurrentOffer, (newVal) => {
        if (newVal) showOffer(newVal)
      })

      onBeforeUnmount(clear)

      return {
        container
      }
    }
  })
</script>

<style lang="scss" scoped>
  .offer-detail-map {
    height: 250px;

    &__canvas {
      width: 100%;
      height: 100%;
      z-index: 0;
    }
  }
</style>
